import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ArrowBack,
  CheckCircle,
  Close,
  ListAlt,
  Tune,
} from "@mui/icons-material";
import { Icon } from "@iconify/react";
import plusFill from "@iconify-icons/eva/plus-fill";
import trashFill from "@iconify-icons/eva/trash-outline";
import undoFill from "@iconify-icons/eva/undo-outline";
import searchFill from "@iconify-icons/eva/search-fill";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import axios from "src/utils/axios";
import { getPrinters } from "src/redux/slices/printer";
import { flattenCategoriesForPicker } from "src/utils/categoryTree";
import ClaudeScanCartaDialog, {
  getPendingCartaScanJobId,
} from "src/components/products/ClaudeScanCartaDialog";
import UnifiedComplementsEditor, {
  normalizeComplement,
} from "./complements/UnifiedComplementsEditor";

const IVA_OPTIONS = [4, 10, 21];

// Ref estable para el fallback de `printers` cuando el store aún no ha
// respondido. Si construyéramos `[]` inline en el render, cada render
// crearía un array nuevo → React.memo del ProductRowComponent
// invalidaría siempre → 500+ filas se re-renderizarían en cada keystroke.
const EMPTY_ARRAY = Object.freeze([]);

let __rowSeq = 0;
const nextTempKey = () => `tmp-${++__rowSeq}-${Date.now()}`;

/**
 * Normaliza un row para el state local del editor. Garantiza shape estable
 * (todos los campos presentes con default razonable).
 */
function normalizeRow(input = {}) {
  return {
    tempKey: input.tempKey || nextTempKey(),
    id: input.id ?? null,
    nombre: input.nombre ?? "",
    descripcion: input.descripcion ?? "",
    precio: input.precio ?? 0,
    idCategory: input.idCategory ?? null,
    newCategoryName: input.newCategoryName ?? "",
    iva: input.iva ?? 10,
    isDeleted: !!input.isDeleted,
    expanded: !!input.expanded,
    alergenos: Array.isArray(input.alergenos) ? input.alergenos.slice() : [],
    complementos: (input.complementos || []).map(normalizeComplement),
    // `??` (no `||`) — un producto existente con `false` explícito lo
    // respetamos. Solo cuando el campo es null/undefined (filas nuevas o
    // legacy sin la propiedad) caemos al default `true`. Sistema simple
    // encaja mejor con la mayoría de complementos de restaurante.
    useSimplePickingSystem: input.useSimplePickingSystem ?? true,
    // Impresora asignada específicamente al producto. `null` = hereda
    // la de la categoría (Category.Printer). En el UI mostramos como
    // "De la categoría" cuando es null.
    printerId: input.printerId ?? null,
  };
}

// normalizeComplement se importa desde ./complements/UnifiedComplementsEditor
// para no duplicar la definición (uso compartido con el editor unificado).

// ─── Diff helpers ───────────────────────────────────────────────────────────
// Para no enviar al backend rows que no han cambiado, snapshot del estado
// inicial (rows con id) y comparamos canonicalizado al guardar. La forma
// canónica ignora tempKey/expanded y normaliza tipos numéricos.

function canonicalComplement(c) {
  return {
    id: c.id ?? null,
    nombre: (c.nombre || "").trim(),
    precio: Number(c.precio) || 0,
    isMandatory: !!c.isMandatory,
    maxQuantity: Number(c.maxQuantity) || 1,
    courseLabelId: c.courseLabelId ?? null,
    // Categoría solo cuenta si NO está vinculado a un producto existente
    // (cuando id != null el backend la ignora).
    idCategory: c.id ? null : c.idCategory ?? null,
    newCategoryName: c.id ? "" : (c.newCategoryName || "").trim(),
  };
}

function canonicalRow(row) {
  return JSON.stringify({
    nombre: (row.nombre || "").trim(),
    descripcion: (row.descripcion || "").trim(),
    precio: Number(row.precio) || 0,
    iva: Number(row.iva) || 10,
    idCategory: row.idCategory ?? null,
    newCategoryName: (row.newCategoryName || "").trim(),
    alergenos: Array.isArray(row.alergenos)
      ? row.alergenos.slice().sort((a, b) => a - b)
      : [],
    complementos: (row.complementos || []).map(canonicalComplement),
    useSimplePickingSystem: !!row.useSimplePickingSystem,
    printerId: row.printerId ?? null,
  });
}

function describeCategory(row, categoryById) {
  if (row.idCategory && categoryById[row.idCategory])
    return categoryById[row.idCategory];
  if (row.newCategoryName) return `${row.newCategoryName} (nueva)`;
  return "—";
}

function diffFields(oldRow, newRow, categoryById) {
  const out = [];
  const oldName = (oldRow.nombre || "").trim();
  const newName = (newRow.nombre || "").trim();
  if (oldName !== newName)
    out.push({ field: "Nombre", from: oldName || "—", to: newName || "—" });

  const oldPrice = Number(oldRow.precio) || 0;
  const newPrice = Number(newRow.precio) || 0;
  if (oldPrice !== newPrice)
    out.push({
      field: "Precio",
      from: `${oldPrice.toFixed(2)}€`,
      to: `${newPrice.toFixed(2)}€`,
    });

  const oldIva = Number(oldRow.iva) || 10;
  const newIva = Number(newRow.iva) || 10;
  if (oldIva !== newIva)
    out.push({ field: "IVA", from: `${oldIva}%`, to: `${newIva}%` });

  const oldCat = describeCategory(oldRow, categoryById);
  const newCat = describeCategory(newRow, categoryById);
  if (oldCat !== newCat)
    out.push({ field: "Categoría", from: oldCat, to: newCat });

  const oldCompsSer = JSON.stringify(
    (oldRow.complementos || []).map(canonicalComplement),
  );
  const newCompsSer = JSON.stringify(
    (newRow.complementos || []).map(canonicalComplement),
  );
  if (oldCompsSer !== newCompsSer) {
    const oldCount = (oldRow.complementos || []).length;
    const newCount = (newRow.complementos || []).length;
    let label;
    if (newCount > oldCount) label = `${oldCount} → ${newCount} (+${newCount - oldCount})`;
    else if (newCount < oldCount) label = `${oldCount} → ${newCount} (-${oldCount - newCount})`;
    else label = `${oldCount} (modificados)`;
    out.push({ field: "Complementos", from: "", to: label, isComp: true });
  }

  const oldSimple = !!oldRow.useSimplePickingSystem;
  const newSimple = !!newRow.useSimplePickingSystem;
  if (oldSimple !== newSimple)
    out.push({
      field: "Recogida simple",
      from: oldSimple ? "Sí" : "No",
      to: newSimple ? "Sí" : "No",
    });

  const oldPrinter = oldRow.printerId ?? null;
  const newPrinter = newRow.printerId ?? null;
  if (oldPrinter !== newPrinter)
    out.push({
      field: "Impresora",
      from: oldPrinter == null ? "De la categoría" : `#${oldPrinter}`,
      to: newPrinter == null ? "De la categoría" : `#${newPrinter}`,
    });

  return out;
}

// buildTemplateFromRow / flattenTemplate se importan desde
// ./complements/UnifiedComplementsEditor — el editor unificado los reutiliza.

function computeChanges(rows, originalById, categoryById) {
  const created = [];
  const updated = [];
  const deleted = [];

  rows.forEach((r) => {
    if (r.id != null && r.isDeleted) {
      const orig = originalById[r.id];
      deleted.push(orig?.row || r);
      return;
    }
    if (r.id == null) {
      // Solo cuenta si tiene nombre — las filas vacías que el usuario añadió y
      // no rellenó ni siquiera deberían persistirse (la validación las bloquea
      // antes, pero filtramos por safety).
      if ((r.nombre || "").trim()) created.push(r);
      return;
    }
    // id != null y no isDeleted → posible update
    const orig = originalById[r.id];
    if (!orig) {
      updated.push({ row: r, original: null, fieldDiffs: [] });
      return;
    }
    if (canonicalRow(r) !== orig.canonical) {
      updated.push({
        row: r,
        original: orig.row,
        fieldDiffs: diffFields(orig.row, r, categoryById),
      });
    }
  });

  return { created, updated, deleted };
}

/**
 * Editor en lista (spreadsheet) para crear/editar productos en bloque.
 * Reutilizable desde:
 *   - el flujo de escaneado (preview tras OCR + IA, antes de persistir)
 *   - el botón "Productos en lista" (edita los existentes y permite añadir nuevos)
 *
 * Wrapper que monta el editor SOLO cuando `open=true`. Sin esto, los `useMemo`
 * pesados (especialmente `productComplementOptions` con N productos) se
 * ejecutarían en cada render del padre aunque el dialog esté cerrado, y al
 * abrir se renderizarían 200+ Autocompletes/Selects de golpe, bloqueando el
 * thread varios segundos en restaurantes con catálogo grande.
 */
export default function BulkProductEditor(props) {
  if (!props.open) return null;
  return <BulkProductEditorInner {...props} />;
}

function BulkProductEditorInner({
  open,
  initialRows,
  categoriesAvailable: categoriesAvailableProp = [],
  productsAvailable: productsAvailableProp = [],
  title = "Productos en lista",
  subtitle = "Edita, crea y borra productos · El listado de complementos es global del restaurante",
  onClose,
  // Cuando true, al montar se abre directamente el dialog de escaneo
  // IA (sin pasar por la vista lista). Usado por el wizard onboarding
  // para llevar al user directo al flujo principal ("sube tu carta").
  autoOpenScanOnMount = false,
}) {
  const { enqueueSnackbar } = useSnackbar();

  // Suscripción directa al store para garantizar que las categorías y
  // productos recién creados aparezcan AL INSTANTE en el editor (lista de
  // categorías izquierda + complementos derecha) sin depender del padre.
  // Antes solo veníamos de prop; aunque el padre re-renderizaba al
  // actualizarse el slice, ocasionalmente había un frame de desfase.
  // El merge prefiere el store cuando está poblado (caso normal) y cae
  // al prop como fallback (carga inicial, tests).
  const categoriesFromStore = useSelector((s) => s.categories?.categories);
  const productsFromStore = useSelector((s) => s.product?.products);
  // Impresoras del restaurante — para el selector inline de cada fila.
  // El default de una fila es "de la categoría" (printerId=null); si el
  // usuario elige una impresora concreta se persiste en Producto.PrinterId
  // y sobrescribe a la de la categoría al comandar.
  const printersFromStore = useSelector((s) => s.printers?.printers);
  const printersDispatch = useDispatch();
  // Si el editor abre y el store de impresoras está vacío (el user no ha
  // pasado nunca por la vista de impresoras en esta sesión), disparamos
  // la carga aquí. Sin esto los selects salen con solo "De la categoría"
  // y da la impresión de que las impresoras no existen.
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(printersFromStore) || printersFromStore.length === 0) {
      printersDispatch(getPrinters());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const categoriesAvailable = useMemo(
    () =>
      Array.isArray(categoriesFromStore) && categoriesFromStore.length > 0
        ? categoriesFromStore
        : categoriesAvailableProp,
    [categoriesFromStore, categoriesAvailableProp],
  );
  const productsAvailable = useMemo(
    () =>
      Array.isArray(productsFromStore) && productsFromStore.length > 0
        ? productsFromStore
        : productsAvailableProp,
    [productsFromStore, productsAvailableProp],
  );
  // El wrapper exterior nos remonta limpio con cada apertura, así que podemos
  // inicializar el state directo desde initialRows (evita el flash de "Sin
  // productos" del primer frame antes de que corra un useEffect de poblado).
  const [rows, setRows] = useState(() =>
    (initialRows || []).map(normalizeRow),
  );
  // Snapshot del estado inicial: { [id]: { row, canonical } }. Lo usamos al
  // guardar para enviar SOLO los productos que realmente han cambiado.
  const [originalRowsById] = useState(() => {
    const map = {};
    (initialRows || []).forEach((r) => {
      if (r != null && r.id != null) {
        const norm = normalizeRow(r);
        map[r.id] = { row: norm, canonical: canonicalRow(norm) };
      }
    });
    return map;
  });
  const [filterCategory, setFilterCategory] = useState(null); // {id, name} or null = todas
  const [filterText, setFilterText] = useState("");
  const [saving, setSaving] = useState(false);
  // Paginación interna — sin esto, con catálogos grandes (>50 productos) el
  // primer render bloquea el thread al montar Autocomplete/Select por fila.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Flujo de pasos:
  //  - 'edit': tabla con campos básicos.
  //  - 'complements': editor de complementos de UN producto (navegado desde
  //    la tabla pulsando el botón de complementos de una fila).
  //  - 'review': vista resumen antes de confirmar el guardado.
  const [step, setStep] = useState("edit");
  const [editingTempKey, setEditingTempKey] = useState(null);
  // F77 · Escaneo IA de carta. El dialog vive en un componente
  // reutilizado (`ClaudeScanCartaDialog`), aquí solo su estado open.
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [changes, setChanges] = useState(null);

  // Al montar, si hay un jobId Claude pendiente en localStorage
  // (F5 durante escaneo), re-abrimos el dialog para reanudar el flujo.
  // También si el caller pide auto-abrir (wizard onboarding).
  useEffect(() => {
    if (getPendingCartaScanJobId() || autoOpenScanOnMount) {
      setScanDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Filtrado para visualizar por categoría sin perder estado ──────────────
  const visibleRows = useMemo(() => {
    let out = rows;
    if (filterCategory && filterCategory.id) {
      out = out.filter((r) => r.idCategory === filterCategory.id);
    }
    if (filterText.trim()) {
      const q = filterText.trim().toLowerCase();
      out = out.filter((r) => (r.nombre || "").toLowerCase().includes(q));
    }
    return out;
  }, [rows, filterCategory, filterText]);

  // Reset de página cuando cambia el filtro — si el usuario está en pág. 4 y
  // filtra a 8 resultados, la pág. 4 quedaría vacía.
  useEffect(() => {
    setPage(0);
  }, [filterCategory, filterText]);

  // Salvavidas: si el row que se está editando desaparece del state
  // (p.ej. el usuario lo borró desde otra vista), volvemos a la lista.
  useEffect(() => {
    if (
      step === "complements" &&
      editingTempKey != null &&
      !rows.some((r) => r.tempKey === editingTempKey)
    ) {
      setEditingTempKey(null);
      setStep("edit");
    }
  }, [rows, editingTempKey, step]);

  const pagedRows = useMemo(
    () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleRows, page, rowsPerPage],
  );

  // ── Mutaciones de fila ────────────────────────────────────────────────────
  // Los mutadores van en useCallback para que su referencia sea ESTABLE
  // entre renders. Es la mitad que necesita `React.memo` en
  // ProductRowComponent — la otra mitad la pone la propia fila pasando el
  // `tempKey` a su capa interna de useCallback. Sin esto, cambiar el
  // `nombre` de una fila re-renderizaba las 500+ filas restantes (cada
  // keystroke creaba nuevas funciones por fila → memo invalidada).
  const updateRow = useCallback(
    (tempKey, patch) =>
      setRows((prev) =>
        prev.map((r) => (r.tempKey === tempKey ? { ...r, ...patch } : r)),
      ),
    [],
  );

  const addRow = () => {
    const newRow = normalizeRow({
      idCategory: filterCategory?.id ?? null,
      iva: 10,
      expanded: false,
    });
    // Insertar al INICIO para que sea visible al instante. Antes iba al
    // final y con 500+ productos quedaba fuera de pantalla / pagina,
    // dando la sensación de que no había pasado nada.
    setRows((prev) => [newRow, ...prev]);
    // Resetear paginación y filtro de texto para garantizar visibilidad.
    // El filtro de categoría sí lo respetamos: la fila nueva nace con esa
    // categoría preasignada (línea arriba), así sigue cumpliéndolo.
    setPage(0);
    setFilterText("");
    // Foco en el campo "Nombre" de la nueva fila tras el render. Sin esto
    // el restaurador tiene que volver a hacer click para escribir.
    setTimeout(() => {
      const input = document.querySelector(
        `[data-row-key="${newRow.tempKey}"] input[type="text"], [data-row-key="${newRow.tempKey}"] input:not([type])`,
      );
      if (input) input.focus();
    }, 0);
  };

  const toggleDelete = useCallback(
    (tempKey) =>
      setRows((prev) =>
        prev
          .map((r) =>
            r.tempKey === tempKey
              ? r.id == null
                ? // Si nunca se persistió, lo quitamos del state directamente.
                  null
                : { ...r, isDeleted: !r.isDeleted }
              : r,
          )
          .filter(Boolean),
      ),
    [],
  );

  const replaceComplementos = (rowKey, complementos) =>
    setRows((prev) =>
      prev.map((r) =>
        r.tempKey === rowKey ? { ...r, complementos } : r,
      ),
    );

  const openComplementsFor = useCallback((rowKey) => {
    setEditingTempKey(rowKey);
    setStep("complements");
  }, []);

  /**
   * Copia los complementos del producto actual al resto de productos cuyo
   * tempKey está en `targetTempKeys`. Genera nuevos tempKey para evitar
   * colisión de keys en React.
   */
  const applyComplementosToMany = (sourceComplementos, targetTempKeys) => {
    const targets = new Set(targetTempKeys);
    setRows((prev) =>
      prev.map((r) =>
        targets.has(r.tempKey)
          ? {
              ...r,
              complementos: sourceComplementos.map((c) =>
                normalizeComplement({ ...c, tempKey: nextTempKey() }),
              ),
            }
          : r,
      ),
    );
  };

  // Recibe el ClaudeCartaScanResult desde `ClaudeScanCartaDialog`.
  // El dialog maneja upload+POST+progreso; aquí solo mapeamos el
  // resultado a filas del editor.
  const handleScanApply = (result) => {
    if (!result?.categories) return;
    const newRows = [];
    for (const cat of result.categories) {
      const catIdMatch = cat.existingCategoryId || null;
      for (const p of (cat.productos || [])) {
        newRows.push(normalizeRow({
          nombre: p.name || "",
          descripcion: p.descripcion || "",
          precio: Number(p.precio) || 0,
          iva: 10,
          idCategory: catIdMatch,
          newCategoryName: catIdMatch ? "" : (cat.name || ""),
          // Metadata IA para el UI (highlight de match).
          _iaExistingProductId: p.existingProductId || null,
          _iaMatchReason: p.matchReason || null,
          _iaConfidence: typeof p.confidence === "number" ? p.confidence : null,
          complementos: (p.complementos || []).map((c) => ({
            id: c.existingComplementId || null,
            nombre: c.name || "",
            precio: Number(c.priceDelta) || 0,
            isMandatory: !!c.required,
            maxQuantity: 1,
          })),
        }));
      }
    }
    if (newRows.length === 0) return;
    // Añadimos al inicio para que el user vea al instante lo escaneado.
    setRows((prev) => [...newRows, ...prev]);
    setPage(0);
    setFilterText("");
    setScanDialogOpen(false);
  };

  // ── Categoría ────────────────────────────────────────────────────────────
  // Mapas para la columna Autocomplete. La opción especial { id: 0, label } se
  // usa cuando el usuario escribe un nombre nuevo (freeSolo).
  // 2026-08-26 · Usa `flattenCategoriesForPicker` para que el dropdown
  // muestre el path completo del árbol N niveles ("Bebidas › Cervezas
  // › Tercios") en vez del nombre plano. El id del picker sigue siendo
  // el id del leaf.
  const categoryOptions = useMemo(() => {
    const flat = flattenCategoriesForPicker(categoriesAvailable || []);
    return flat.map((n) => ({ id: n.id, label: n.pathLabel, depth: n.depth }));
  }, [categoriesAvailable]);

  // ── Complementos ────────────────────────────────────────────────────────
  // Opciones de productos existentes para usar como complementos. Cualquier
  // producto del restaurante puede ser complemento de otro (típico: bebidas,
  // salsas, extras compartidos entre platos). Agrupamos por categoría en el
  // dropdown para que el usuario los encuentre rápido.
  const categoryById = useMemo(
    () =>
      Object.fromEntries(
        (categoriesAvailable || []).map((c) => [c.id, c.name]),
      ),
    [categoriesAvailable],
  );

  // Impresora heredada por categoría (id → printerId). Se usa como
  // placeholder del selector inline de impresora en la fila del bulk:
  // cuando el producto no tiene `printerId` propio, el select muestra
  // "De la categoría (X)" apuntando a esta.
  const categoryPrinterById = useMemo(
    () =>
      Object.fromEntries(
        (categoriesAvailable || []).map((c) => [c.id, c.printer ?? null]),
      ),
    [categoriesAvailable],
  );

  // Producto del catálogo del restaurante por id (no del state local). Se usa
  // en la vista de complementos para resolver la categoría de un complemento
  // vinculado.
  const productById = useMemo(
    () =>
      Object.fromEntries(
        (productsAvailable || [])
          .filter((p) => p && p.id != null)
          .map((p) => [p.id, p]),
      ),
    [productsAvailable],
  );

  const editingRow = useMemo(
    () =>
      editingTempKey != null
        ? rows.find((r) => r.tempKey === editingTempKey)
        : null,
    [rows, editingTempKey],
  );

  const handleCategoryChange = useCallback((rowKey, value) => {
    if (!value) {
      updateRow(rowKey, { idCategory: null, newCategoryName: "" });
      return;
    }
    if (typeof value === "string") {
      // freeSolo: usuario escribió un nombre nuevo
      updateRow(rowKey, { idCategory: null, newCategoryName: value });
      return;
    }
    if (value.id) {
      updateRow(rowKey, { idCategory: value.id, newCategoryName: "" });
    } else {
      // Auto-creada inline (id 0)
      updateRow(rowKey, { idCategory: null, newCategoryName: value.label || "" });
    }
  }, [updateRow]);

  const categoryValueFor = (row) => {
    if (row.idCategory) {
      return categoryOptions.find((o) => o.id === row.idCategory) || null;
    }
    if (row.newCategoryName) return row.newCategoryName;
    return null;
  };

  // ── Validación + submit ──────────────────────────────────────────────────
  const invalidRows = useMemo(
    () => rows.filter((r) => !r.isDeleted && (!r.nombre || r.nombre.trim() === "")),
    [rows],
  );

  const visibleCount = visibleRows.length;
  const liveProductsCount = rows.filter((r) => !r.isDeleted).length;
  const totalComplementsCount = rows.reduce(
    (acc, r) => acc + (r.complementos?.length || 0),
    0,
  );

  const rowToPayload = (r) => ({
    Id: r.id ?? null,
    Nombre: r.nombre,
    Descripcion: r.descripcion || null,
    Precio: Number(r.precio) || 0,
    Iva: Number(r.iva) || 10,
    IdCategory: r.idCategory ?? null,
    NewCategoryName: r.idCategory ? null : r.newCategoryName || null,
    IsDeleted: !!r.isDeleted,
    Alergenos: Array.isArray(r.alergenos) ? r.alergenos : [],
    UseSimplePickingSystem: !!r.useSimplePickingSystem,
    PrinterId: r.printerId ?? null,
    Complementos: (r.complementos || []).map((c) => ({
      Id: c.id ?? null,
      Nombre: c.nombre,
      Precio: Number(c.precio) || 0,
      IsMandatory: !!c.isMandatory,
      MaxQuantity: Number(c.maxQuantity) || 1,
      CourseLabelId: c.courseLabelId ?? null,
      // Solo viaja categoría para complementos nuevos. Si está vinculado a
      // un producto existente (c.id != null), el backend ignora estos.
      IdCategory: c.id ? null : c.idCategory ?? null,
      NewCategoryName: c.id ? null : c.idCategory ? null : c.newCategoryName || null,
    })),
  });

  const handleSubmit = () => {
    if (invalidRows.length > 0) {
      enqueueSnackbar(
        `Hay ${invalidRows.length} fila(s) sin nombre. Rellénalas o elimínalas.`,
        { variant: "warning" },
      );
      return;
    }
    const c = computeChanges(rows, originalRowsById, categoryById);
    if (
      c.created.length === 0 &&
      c.updated.length === 0 &&
      c.deleted.length === 0
    ) {
      enqueueSnackbar("No hay cambios que guardar", { variant: "info" });
      return;
    }
    setChanges(c);
    setStep("review");
  };

  const confirmSave = async () => {
    if (!changes) return;
    setSaving(true);
    try {
      const dirty = [
        ...changes.created,
        ...changes.updated.map((u) => u.row),
        ...changes.deleted,
      ];
      const payload = { Productos: dirty.map(rowToPayload) };
      await axios.post("productos/BulkSave", payload);
      enqueueSnackbar("Productos guardados", { variant: "success" });
      onClose && onClose(true);
    } catch (err) {
      enqueueSnackbar(err?.response?.data || "Error guardando productos", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Guard contra pérdida de cambios ──────────────────────────────────────
  // Bug 2026-07-28 · cerrar el editor con Esc/X/backdrop/Cancelar/F5 tiraba
  // los cambios en silencio. Ahora:
  //   • Cierre desde UI → modal de confirmación in-app.
  //   • Recarga / cerrar pestaña → beforeunload nativo del browser.
  // El check reusa `computeChanges` on-demand (no continuo — pesado con 500+
  // filas si se recalcula en cada render).
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const hasPendingChanges = () => {
    try {
      const c = computeChanges(rows, originalRowsById, categoryById);
      return c.created.length + c.updated.length + c.deleted.length > 0;
    } catch {
      return false;
    }
  };
  const requestClose = () => {
    if (saving) return; // en pleno guardado — bloquear cierres
    if (hasPendingChanges()) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose && onClose(false);
  };
  useEffect(() => {
    if (!open) return undefined;
    const beforeUnload = (e) => {
      if (!hasPendingChanges()) return undefined;
      // Estándar: preventDefault + returnValue = string vaticina el prompt.
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rows, originalRowsById, categoryById]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Backdrop y Esc pasan por el guard de cambios sin guardar.
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          requestClose();
          return;
        }
        requestClose();
      }}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: "92vh" } }}
    >
      {/* ── Title ── */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ListAlt color="primary" />
            <Box>
              <Typography variant="h6">{title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={requestClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Toolbar (solo en paso 'edit') ── */}
      {step === "edit" && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.neutral",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              size="small"
              placeholder="Buscar…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={searchFill} width={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            <Autocomplete
              size="small"
              options={categoryOptions}
              value={filterCategory}
              onChange={(_, v) => setFilterCategory(v)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Filtrar categoría" />
              )}
              sx={{ minWidth: 240 }}
            />
            <Box sx={{ flex: 1 }} />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                variant="outlined"
                label={`${liveProductsCount} producto${liveProductsCount !== 1 ? "s" : ""}`}
              />
              <Chip
                size="small"
                variant="outlined"
                color="info"
                label={`${totalComplementsCount} complemento${totalComplementsCount !== 1 ? "s" : ""}`}
              />
              {filterCategory || filterText ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={`${visibleCount} visible${visibleCount !== 1 ? "s" : ""}`}
                />
              ) : null}
              {/* F77 · Escanear carta con IA — sube 1-3 imágenes/PDF y
                  Claude devuelve productos detectados con matching contra
                  el catálogo existente (existingProductId/CategoryId).
                  Filas nuevas se añaden en rojo si son novedad, en verde
                  si asocian a un producto que YA existe. */}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Icon icon="eva:cloud-upload-outline" width={18} />}
                onClick={() => setScanDialogOpen(true)}
              >
                Escanear con IA
              </Button>
              <Button
                variant="contained"
                startIcon={<Icon icon={plusFill} width={18} />}
                onClick={addRow}
              >
                Añadir fila
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {step === "review" ? (
          <ReviewPanel changes={changes} categoryById={categoryById} />
        ) : step === "complements" && editingRow ? (
          <ProductComplementsView
            row={editingRow}
            allRows={rows}
            productById={productById}
            productsAvailable={productsAvailable}
            categoriesAvailable={categoriesAvailable}
            categoryById={categoryById}
            onReplaceComplementos={(complementos) =>
              replaceComplementos(editingRow.tempKey, complementos)
            }
            onUpdateRow={(patch) => updateRow(editingRow.tempKey, patch)}
            onApplyToMany={applyComplementosToMany}
            onBack={() => {
              setEditingTempKey(null);
              setStep("edit");
            }}
          />
        ) : visibleRows.length === 0 ? (
          <Alert severity="info" sx={{ m: 3 }}>
            {rows.length === 0
              ? "Sin productos. Pulsa 'Añadir fila' para empezar."
              : "Ningún producto coincide con el filtro."}
          </Alert>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>Nombre</TableCell>
                  <TableCell sx={{ width: 120 }}>Precio</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Categoría</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Impresora</TableCell>
                  <TableCell sx={{ width: 110 }}>IVA</TableCell>
                  <TableCell sx={{ width: 130 }} align="center">
                    Complementos
                  </TableCell>
                  <TableCell sx={{ width: 90 }} align="center">
                    <Tooltip title="Sistema de recogida simple — todos los complementos en un solo pase">
                      <span>Simple</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ width: 64 }} align="center">
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => (
                  <ProductRowComponent
                    key={row.tempKey}
                    row={row}
                    categoryOptions={categoryOptions}
                    categoryValue={categoryValueFor(row)}
                    printers={printersFromStore && printersFromStore.length > 0 ? printersFromStore : EMPTY_ARRAY}
                    // Impresora heredada de la categoría — se muestra
                    // como placeholder cuando `row.printerId == null`.
                    categoryPrinterId={
                      categoryPrinterById?.[row.idCategory] ?? null
                    }
                    onUpdate={updateRow}
                    onChangeCategory={handleCategoryChange}
                    onToggleDelete={toggleDelete}
                    onOpenComplements={openComplementsFor}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {step === "edit" && visibleRows.length > 0 && (
          <TablePagination
            component="div"
            count={visibleRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
            sx={{ borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}
          />
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Box sx={{ mr: "auto" }}>
          {step === "review" ? (
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {changes
                ? `${changes.created.length} a crear · ${changes.updated.length} a editar · ${changes.deleted.length} a eliminar`
                : ""}
            </Typography>
          ) : step === "complements" && editingRow ? (
            <Typography variant="caption" color="text.secondary">
              Cambios aplicados al instante — vuelve a la lista para seguir
              editando otros productos
            </Typography>
          ) : invalidRows.length > 0 ? (
            <Typography variant="caption" color="error.main" fontWeight={600}>
              {invalidRows.length} fila(s) sin nombre — rellénalas o elimínalas
              para guardar
            </Typography>
          ) : liveProductsCount > 0 ? (
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              Se guardarán {liveProductsCount} producto
              {liveProductsCount !== 1 ? "s" : ""}
              {totalComplementsCount > 0
                ? ` · ${totalComplementsCount} complemento${totalComplementsCount !== 1 ? "s" : ""}`
                : ""}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Añade al menos un producto para guardar
            </Typography>
          )}
        </Box>
        {step === "review" ? (
          <>
            <Button onClick={() => setStep("edit")} disabled={saving}>
              Volver
            </Button>
            <LoadingButton
              variant="contained"
              loading={saving}
              onClick={confirmSave}
            >
              Confirmar y guardar
            </LoadingButton>
          </>
        ) : step === "complements" ? (
          <Button
            variant="contained"
            onClick={() => {
              setEditingTempKey(null);
              setStep("edit");
            }}
          >
            Volver a la lista
          </Button>
        ) : (
          <>
            <Button onClick={requestClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={liveProductsCount === 0 || invalidRows.length > 0}
            >
              Revisar cambios
            </Button>
          </>
        )}
      </DialogActions>
      {/* F77 · Dialog de escaneo IA. Sube 1-3 imágenes/PDF, llama a
          POST /ReadCarta/claude y auto-pobla filas con matching contra
          el catálogo existente. Coincidencias detectadas → chip verde. */}
      <ClaudeScanCartaDialog
        open={scanDialogOpen}
        onClose={() => setScanDialogOpen(false)}
        onScanned={handleScanApply}
        autoApplyOnComplete={autoOpenScanOnMount}
      />

      {/* Confirmación de descarte de cambios sin guardar. */}
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cambios sin guardar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Tienes cambios en la carta que aún no has guardado. Si cierras
            ahora se perderán todos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDiscardOpen(false)} color="inherit">
            Seguir editando
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setConfirmDiscardOpen(false);
              onClose && onClose(false);
            }}
          >
            Descartar y salir
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmDiscardOpen(false);
              handleSubmit();
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// ─── ReviewPanel ────────────────────────────────────────────────────────────
// Vista de resumen del paso 'review': muestra qué se creará / editará /
// eliminará con el detalle de los campos modificados, agrupado en bloques
// con borde de color para que el usuario pueda confirmar de un vistazo.

function ReviewPanel({ changes, categoryById }) {
  if (!changes) return null;
  const { created, updated, deleted } = changes;

  if (created.length === 0 && updated.length === 0 && deleted.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No hay cambios pendientes.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2.5, overflow: "auto" }}>
      <Stack spacing={2}>
        {created.length > 0 && (
          <ReviewCard
            color="success.main"
            bgColor="success.lighter"
            title={`Crear ${created.length} producto${created.length !== 1 ? "s" : ""}`}
          >
            <Stack divider={<Box sx={{ borderBottom: "1px dashed", borderColor: "divider" }} />}>
              {created.map((r) => (
                <Box key={r.tempKey} sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {r.nombre || "(sin nombre)"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Number(r.precio || 0).toFixed(2)}€ · IVA {r.iva}% ·{" "}
                    {describeCategory(r, categoryById)}
                    {r.complementos?.length
                      ? ` · ${r.complementos.length} complemento${r.complementos.length !== 1 ? "s" : ""}`
                      : ""}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </ReviewCard>
        )}

        {updated.length > 0 && (
          <ReviewCard
            color="info.main"
            bgColor="info.lighter"
            title={`Editar ${updated.length} producto${updated.length !== 1 ? "s" : ""}`}
          >
            <Stack divider={<Box sx={{ borderBottom: "1px dashed", borderColor: "divider" }} />}>
              {updated.map((u) => (
                <Box key={u.row.tempKey} sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {u.row.nombre}
                  </Typography>
                  {u.fieldDiffs.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      Cambio sin detallar
                    </Typography>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.5 }}
                    >
                      {u.fieldDiffs.map((d, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          variant="outlined"
                          color="info"
                          label={
                            d.isComp
                              ? `${d.field}: ${d.to}`
                              : `${d.field}: ${d.from} → ${d.to}`
                          }
                          sx={{ height: 22, fontSize: 11 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          </ReviewCard>
        )}

        {deleted.length > 0 && (
          <ReviewCard
            color="error.main"
            bgColor="error.lighter"
            title={`Eliminar ${deleted.length} producto${deleted.length !== 1 ? "s" : ""}`}
          >
            <Stack divider={<Box sx={{ borderBottom: "1px dashed", borderColor: "divider" }} />}>
              {deleted.map((r) => (
                <Box key={r.tempKey || r.id} sx={{ py: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ textDecoration: "line-through" }}
                  >
                    {r.nombre}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {describeCategory(r, categoryById)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </ReviewCard>
        )}
      </Stack>
    </Box>
  );
}

function ReviewCard({ color, bgColor, title, children }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: color,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: bgColor,
          borderBottom: "1px solid",
          borderColor: color,
        }}
      >
        <Typography variant="subtitle2" sx={{ color }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2 }}>{children}</Box>
    </Box>
  );
}

// ─── Subcomponente: fila producto (sin sub-filas) ──────────────────────────
// Los complementos se editan en una vista dedicada (step 'complements')
// que se abre desde el botón de la columna "Complementos".

// ── Constantes a nivel módulo para evitar que Emotion re-genere clases
//    CSS cada render. Cada `sx={{...}}` inline crea un objeto nuevo →
//    emotion hashea (murmur2) e inserta una regla. Con 25 filas x 5 sx
//    eran ~125 inserciones CSS por keystroke. Movido fuera = 0.
const ROW_SX_DELETED = {
  bgcolor: "action.disabledBackground",
  textDecoration: "line-through",
  opacity: 0.6,
};
const ROW_SX_EMPTY = {};
const SX_W100 = { width: 100 };
const SX_W90 = { width: 90 };
const SX_MINW200 = { minWidth: 200 };
const SX_TUNE_ICON = { fontSize: 16 };
const PRICE_INPUT_PROPS = { step: 0.05, min: 0 };
// JSX estable también — el endAdornment se reutiliza entre filas.
const PRICE_END_ADORNMENT = (
  <InputAdornment position="end">€</InputAdornment>
);
const PRICE_INPUT_INPUTPROPS = { endAdornment: PRICE_END_ADORNMENT };
// Funciones puras del Autocomplete — sin closure dependiente. Definidas
// fuera para que la referencia sea estable y Autocomplete no necesite
// re-comparar memoizaciones internas en cada render.
const getCategoryOptionLabel = (o) =>
  typeof o === "string" ? o : o?.label || "";
const isCategoryOptionEqualToValue = (o, v) =>
  typeof o === "string" || typeof v === "string" ? o === v : o?.id === v?.id;
const renderCategoryInput = (params) => (
  <TextField {...params} variant="standard" placeholder="—" />
);

// ProductRow se memoiza para que cambiar el `nombre` de UNA fila no
// rerenderice las 500+ filas restantes (cada TextField re-render con
// keystroke saturaba el main thread y producía lag perceptible).
// La memoización funciona porque el padre estabiliza los handlers con
// useCallback y `categoryOptions` ya viene de un useMemo.
const ProductRowComponent = memo(function ProductRowComponent({
  row,
  categoryOptions,
  categoryValue,
  printers,                  // Impresoras del restaurante [{id, name}]
  categoryPrinterId,         // Impresora heredada de la categoría (int | null)
  onUpdate,                  // (tempKey, patch) — estable en el padre
  onChangeCategory,          // (tempKey, value) — estable en el padre
  onToggleDelete,            // (tempKey) — estable
  onOpenComplements,         // (tempKey) — estable
}) {
  const { tempKey } = row;

  // Buffers LOCALES para nombre y precio: con 572 filas, dispatchar setRows
  // en cada keystroke rederivaba todo el árbol del padre (filtros, paginador,
  // header, chips) y latencia perceptible. Mantener el draft local y
  // confirmar solo al blur reduce a 1 setRows por edición completa.
  // Sincronización: cuando llega un row.nombre/precio NUEVO desde fuera
  // (cambio remoto, undo de delete, etc.), reseteamos el buffer.
  const [nameDraft, setNameDraft] = useState(row.nombre ?? "");
  const [priceDraft, setPriceDraft] = useState(row.precio ?? 0);
  useEffect(() => {
    setNameDraft(row.nombre ?? "");
  }, [row.nombre]);
  useEffect(() => {
    setPriceDraft(row.precio ?? 0);
  }, [row.precio]);

  const commitName = useCallback(() => {
    if (nameDraft !== row.nombre) onUpdate(tempKey, { nombre: nameDraft });
  }, [nameDraft, row.nombre, onUpdate, tempKey]);
  const commitPrice = useCallback(() => {
    if (priceDraft !== row.precio) onUpdate(tempKey, { precio: priceDraft });
  }, [priceDraft, row.precio, onUpdate, tempKey]);

  const handleIva = useCallback(
    (e) => onUpdate(tempKey, { iva: Number(e.target.value) }),
    [onUpdate, tempKey],
  );
  const handlePrinter = useCallback(
    (e) => {
      // Valor 0 (opción "De la categoría") → null en el modelo.
      const raw = e.target.value;
      const printerId = raw === 0 || raw === "" || raw === null ? null : Number(raw);
      onUpdate(tempKey, { printerId });
    },
    [onUpdate, tempKey],
  );
  const handleSimpleToggle = useCallback(
    (e) => onUpdate(tempKey, { useSimplePickingSystem: e.target.checked }),
    [onUpdate, tempKey],
  );
  const handleCategoryChange = useCallback(
    (_, v) => onChangeCategory(tempKey, v),
    [onChangeCategory, tempKey],
  );
  const handleCategoryInput = useCallback(
    (_, v, reason) => {
      if (reason === "input") onChangeCategory(tempKey, v);
    },
    [onChangeCategory, tempKey],
  );
  const handleToggleDelete = useCallback(
    () => onToggleDelete(tempKey),
    [onToggleDelete, tempKey],
  );
  const handleOpenComplements = useCallback(
    () => onOpenComplements(tempKey),
    [onOpenComplements, tempKey],
  );

  const rowSx = row.isDeleted ? ROW_SX_DELETED : ROW_SX_EMPTY;

  return (
    <TableRow sx={rowSx} data-row-key={tempKey}>
      <TableCell>
        <TextField
          variant="standard"
          fullWidth
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitName}
          disabled={row.isDeleted}
          error={!row.isDeleted && !(nameDraft || "").trim()}
        />
      </TableCell>
      <TableCell>
        <TextField
          variant="standard"
          type="number"
          value={priceDraft}
          onChange={(e) => setPriceDraft(e.target.value)}
          onBlur={commitPrice}
          disabled={row.isDeleted}
          inputProps={PRICE_INPUT_PROPS}
          InputProps={PRICE_INPUT_INPUTPROPS}
          sx={SX_W100}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          size="small"
          freeSolo
          options={categoryOptions}
          value={categoryValue}
          disabled={row.isDeleted}
          onChange={handleCategoryChange}
          onInputChange={handleCategoryInput}
          getOptionLabel={getCategoryOptionLabel}
          isOptionEqualToValue={isCategoryOptionEqualToValue}
          renderInput={renderCategoryInput}
          sx={SX_MINW200}
        />
      </TableCell>
      <TableCell>
        {/* Selector inline de impresora. Opción 0 = "De la categoría",
            que persiste como `printerId: null` en el modelo (el backend
            hereda la de `Category.Printer` al comandar). El label
            muestra el nombre de la impresora heredada entre paréntesis
            para que el camarero sepa a qué apunta el default sin abrir
            otro sitio. */}
        <Select
          variant="standard"
          value={row.printerId ?? 0}
          onChange={handlePrinter}
          disabled={row.isDeleted}
          sx={{ minWidth: 160 }}
          size="small"
        >
          <MenuItem value={0}>
            <em>
              De la categoría
              {categoryPrinterId != null &&
                (() => {
                  const inherited = (printers || []).find(
                    (p) => p.id === categoryPrinterId,
                  );
                  return inherited ? ` (${inherited.name})` : "";
                })()}
            </em>
          </MenuItem>
          {(printers || []).map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <Select
          variant="standard"
          value={row.iva}
          onChange={handleIva}
          disabled={row.isDeleted}
          sx={SX_W90}
        >
          {IVA_OPTIONS.map((v) => (
            <MenuItem key={v} value={v}>
              {v}%
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell align="center">
        <Button
          size="small"
          variant={row.complementos.length > 0 ? "outlined" : "text"}
          color={row.complementos.length > 0 ? "primary" : "inherit"}
          startIcon={<Tune sx={SX_TUNE_ICON} />}
          onClick={handleOpenComplements}
          disabled={row.isDeleted}
        >
          {row.complementos.length > 0
            ? `${row.complementos.length}`
            : "Añadir"}
        </Button>
      </TableCell>
      <TableCell align="center">
        {/* Switch "Sistema simple". Disabled si el producto no tiene
            complementos: el flag no aplica en ese caso y el UI lo
            comunica atenuándolo. Nuevos productos arrancan en `true`
            desde `normalizeRow` — el usuario lo desactiva aquí si
            quiere flujo multi-fase. */}
        <Tooltip
          title={
            row.complementos.length === 0
              ? "Sin complementos: el sistema simple no aplica"
              : row.useSimplePickingSystem
                ? "Complementos en un solo pase"
                : "Complementos por fases (clásico)"
          }
        >
          <span>
            <Switch
              size="small"
              checked={!!row.useSimplePickingSystem}
              onChange={handleSimpleToggle}
              disabled={row.isDeleted || row.complementos.length === 0}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Tooltip title={row.isDeleted ? "Restaurar" : "Eliminar"}>
          <IconButton size="small" onClick={handleToggleDelete}>
            <Icon
              icon={row.isDeleted ? undoFill : trashFill}
              width={18}
              color={row.isDeleted ? undefined : "#d32f2f"}
            />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
});

// ─── ProductComplementsView (layout 3 paneles) ─────────────────────────────
// Vista dedicada a editar los complementos del producto activo, con el mismo
// layout que el antiguo "Asignar complementos en bloque":
//
//   Panel 1 (izq, 280px) — Productos destino:
//     - "Volver a productos" + tarjeta destacada del producto activo.
//     - Filtro multi-categoría + buscador + lista checkable de OTROS
//       productos del lote.
//     - Botón "Aplicar config a N productos" replica los complementos del
//       producto activo a los marcados.
//     - Botón "Cargar plantilla de…" vuelca la config de otro producto
//       (del catálogo) sobre el activo.
//
//   Panel 2 (centro, flex) — Configuración:
//     - Sección Alérgenos (chips toggleables).
//     - DragDropContext con un Accordion por categoría: drag handle +
//       checkbox master + Switch obligatorio + max + lista checkable de
//       productos con precio.
//     - Solo se muestran categorías que tengan productos activos.
//
//   Panel 3 (der, 300px) — Preview cliente:
//     - Alérgenos + grupos con productos seleccionados, en formato Card
//       similar a como se renderiza al cliente final.

function ProductComplementsView({
  row,
  allRows,
  productById,
  productsAvailable,
  categoriesAvailable,
  categoryById,
  onReplaceComplementos,
  onUpdateRow,
  onApplyToMany,
  onBack,
}) {
  // ── Panel 1: estado local de filtros + selección de destinos extra ──
  const [catFilter, setCatFilter] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [extraTargets, setExtraTargets] = useState(() => new Set());

  // ── Cargar plantilla desde otro producto (panel 1) ──
  const handleLoadTemplate = (sourceProduct) => {
    if (!sourceProduct) return;
    const sourceComplementos = (sourceProduct.complementos || []).map((c) =>
      normalizeComplement({
        id: c.idComplemento ?? c.id,
        nombre: c.nombre || c.name || "",
        precio: c.precio ?? c.price ?? 0,
        isMandatory: !!(c.isMandatory ?? c.IsMandatory),
        maxQuantity: c.maxQuantity ?? c.MaxQuantity ?? 1,
        courseLabelId:
          c.complementCourseLabelId ??
          c.courseLabelId ??
          c.CourseLabelId ??
          null,
      }),
    );
    onReplaceComplementos(sourceComplementos);
  };

  const handleApplyToSelected = () => {
    if (extraTargets.size === 0) return;
    onApplyToMany(row.complementos, Array.from(extraTargets));
    setExtraTargets(new Set());
  };

  const candidates = useMemo(
    () =>
      (allRows || []).filter(
        (r) =>
          r.tempKey !== row.tempKey &&
          !r.isDeleted &&
          (r.nombre || "").trim(),
      ),
    [allRows, row.tempKey],
  );

  const visibleCandidates = useMemo(() => {
    let list = candidates;
    if (catFilter.length > 0) {
      const ids = new Set(catFilter.map((c) => c.id));
      list = list.filter((r) => ids.has(r.idCategory));
    }
    const q = productSearch.trim().toLowerCase();
    if (q) list = list.filter((r) => (r.nombre || "").toLowerCase().includes(q));
    return list;
  }, [candidates, catFilter, productSearch]);

  const candidateCategoryOptions = useMemo(() => {
    const ids = new Set(candidates.map((r) => r.idCategory).filter(Boolean));
    return (categoriesAvailable || []).filter((c) => ids.has(c.id));
  }, [candidates, categoriesAvailable]);

  const toggleTarget = (tempKey) => {
    setExtraTargets((prev) => {
      const next = new Set(prev);
      next.has(tempKey) ? next.delete(tempKey) : next.add(tempKey);
      return next;
    });
  };

  const allVisibleTargetsSelected =
    visibleCandidates.length > 0 &&
    visibleCandidates.every((r) => extraTargets.has(r.tempKey));
  const someTargetsSelected = visibleCandidates.some((r) =>
    extraTargets.has(r.tempKey),
  );
  const toggleAllVisibleTargets = () => {
    setExtraTargets((prev) => {
      const next = new Set(prev);
      if (allVisibleTargetsSelected) {
        visibleCandidates.forEach((r) => next.delete(r.tempKey));
      } else {
        visibleCandidates.forEach((r) => next.add(r.tempKey));
      }
      return next;
    });
  };

  const productsWithComplements = useMemo(
    () =>
      (productsAvailable || []).filter(
        (p) =>
          p && p.isActive !== false && (p.complementos || []).length > 0,
      ),
    [productsAvailable],
  );

  // Métricas para la tarjeta "EDITANDO" del panel 1.
  const totalChecked = (row.complementos || []).filter((c) => c.id != null).length;
  const activeGroupCount = useMemo(() => {
    const cats = new Set();
    (row.complementos || []).forEach((c) => {
      if (c.id == null) return;
      const cat = productById?.[c.id]?.idCategory;
      if (cat != null) cats.add(cat);
    });
    return cats.size;
  }, [row.complementos, productById]);

  return (
    <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* ══ PANEL 1 — Productos destino ══ */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Button
            size="small"
            startIcon={<ArrowBack fontSize="small" />}
            onClick={onBack}
            sx={{ mb: 1.5 }}
          >
            Volver a productos
          </Button>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderColor: "primary.main",
              bgcolor: "primary.lighter",
              borderRadius: 1.5,
            }}
          >
            <Typography
              variant="caption"
              color="primary.main"
              fontWeight={600}
              display="block"
            >
              EDITANDO
            </Typography>
            <Typography variant="subtitle2" noWrap>
              {row.nombre || "(sin nombre)"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {activeGroupCount} grupo{activeGroupCount !== 1 ? "s" : ""} ·{" "}
              {totalChecked} complemento{totalChecked !== 1 ? "s" : ""}
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            Aplicar también a:
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={candidateCategoryOptions}
            value={catFilter}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setCatFilter(v)}
            disableCloseOnSelect
            renderTags={(value, getTagProps) =>
              value.map((cat, index) => (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  size="small"
                  color="primary"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={catFilter.length === 0 ? "Todas las categorías" : ""}
              />
            )}
            sx={{ mb: 1 }}
          />
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar producto…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} width={16} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Checkbox
            size="small"
            checked={allVisibleTargetsSelected}
            indeterminate={someTargetsSelected && !allVisibleTargetsSelected}
            onChange={toggleAllVisibleTargets}
          />
          <Typography variant="caption" color="text.secondary">
            {extraTargets.size > 0
              ? `${extraTargets.size} seleccionado${extraTargets.size !== 1 ? "s" : ""}`
              : `Seleccionar todos (${visibleCandidates.length})`}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {visibleCandidates.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {candidates.length === 0
                  ? "No hay otros productos en el lote."
                  : "Ningún producto coincide con el filtro."}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {visibleCandidates.map((r) => {
                const isSel = extraTargets.has(r.tempKey);
                const catName = r.idCategory
                  ? categoryById?.[r.idCategory] || "Sin categoría"
                  : r.newCategoryName
                  ? `${r.newCategoryName} (nueva)`
                  : "Sin categoría";
                return (
                  <ListItem
                    key={r.tempKey}
                    button
                    onClick={() => toggleTarget(r.tempKey)}
                    selected={isSel}
                    sx={{ px: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox
                        size="small"
                        checked={isSel}
                        sx={{ p: 0 }}
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={r.nombre || "(sin nombre)"}
                      secondary={catName}
                      primaryTypographyProps={{ variant: "body2", noWrap: true }}
                      secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                    />
                    {isSel && (
                      <CheckCircle
                        sx={{ fontSize: 16, color: "primary.main", ml: 0.5 }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            disabled={extraTargets.size === 0}
            onClick={handleApplyToSelected}
            sx={{ mb: 1 }}
          >
            Aplicar a {extraTargets.size} producto{extraTargets.size !== 1 ? "s" : ""}
          </Button>
          <Autocomplete
            size="small"
            options={productsWithComplements}
            getOptionLabel={(p) => p?.nombre || ""}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            onChange={(_, v) => handleLoadTemplate(v)}
            blurOnSelect
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Cargar plantilla de…"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Tune sx={{ fontSize: 16 }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Box>

      {/* ══ PANELES 2+3 — Editor unificado (config + preview) ══ */}
      <UnifiedComplementsEditor
        row={row}
        productsAvailable={productsAvailable}
        categoriesAvailable={categoriesAvailable}
        productById={productById}
        onReplaceComplementos={onReplaceComplementos}
        onUpdateRow={onUpdateRow}
        showPreview
      />
    </Box>
  );
}

// ConfigGroupAccordion, ProductComplementCell y ProductPreviewPanel
// se importan desde ./complements/UnifiedComplementsEditor (extraídos
// para compartir con AddComplements del editor individual de producto).
